import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, Edit2 } from 'lucide-react';
import { Upload, X } from './icons';
import api from '../services/api';

export default function SubirImagenEvidencia({
    evidenciaId,
    onImagenSubida,
    onClose,
    isOpen
}) {
    const [archivos, setArchivos] = useState([]);
    const [comentarios, setComentarios] = useState({});
    const [editandoComentario, setEditandoComentario] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    if (!isOpen) return null;

    const manejarSeleccionArchivos = (files) => {
        const archivosArray = Array.from(files);
        const archivosValidos = archivosArray.filter(archivo => {
            const esImagen = archivo.type.startsWith('image/');
            const esValido = esImagen && archivo.size <= 10 * 1024 * 1024; // 10MB
            return esValido;
        });

        setArchivos(prev => {
            const nuevosArchivos = [...prev, ...archivosValidos];
            // Inicializar comentarios vacíos para los nuevos archivos
            const nuevosComentarios = { ...comentarios };
            archivosValidos.forEach((_, index) => {
                const indiceReal = prev.length + index;
                nuevosComentarios[indiceReal] = '';
            });
            setComentarios(nuevosComentarios);
            return nuevosArchivos;
        });
    };

    const manejarCambioArchivo = (e) => {
        manejarSeleccionArchivos(e.target.files);
    };

    const manejarDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const manejarDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const manejarDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        manejarSeleccionArchivos(e.dataTransfer.files);
    };

    const manejarPaste = (e) => {
        const items = e.clipboardData.items;
        const archivos = [];

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith('image/')) {
                const archivo = item.getAsFile();
                if (archivo && archivo.size <= 10 * 1024 * 1024) {
                    archivos.push(archivo);
                }
            }
        }

        if (archivos.length > 0) {
            manejarSeleccionArchivos(archivos);
        }
    };

    const eliminarArchivo = (index) => {
        setArchivos(prev => prev.filter((_, i) => i !== index));
        setComentarios(prev => {
            const nuevosComentarios = { ...prev };
            delete nuevosComentarios[index];
            // Reindexar los comentarios después del índice eliminado
            const comentariosReindexados = {};
            Object.keys(nuevosComentarios).forEach(key => {
                const indice = parseInt(key);
                if (indice < index) {
                    comentariosReindexados[indice] = nuevosComentarios[key];
                } else if (indice > index) {
                    comentariosReindexados[indice - 1] = nuevosComentarios[key];
                }
            });
            return comentariosReindexados;
        });
    };

    const manejarCambioComentario = (index, comentario) => {
        setComentarios(prev => ({
            ...prev,
            [index]: comentario
        }));
    };

    const iniciarEdicionComentario = (index) => {
        setEditandoComentario(index);
    };

    const finalizarEdicionComentario = () => {
        setEditandoComentario(null);
    };

    const subirImagenes = async () => {
        if (archivos.length === 0) return;

        setSubiendo(true);
        try {
            for (let i = 0; i < archivos.length; i++) {
                const archivo = archivos[i];
                const comentario = comentarios[i] || '';

                const formData = new FormData();
                formData.append('archivo', archivo);
                formData.append('evidenciaId', evidenciaId);
                formData.append('descripcion', comentario);

                await api.post(`/EvidenciaSeguridad/${evidenciaId}/subir-imagen`, formData);
            }

            onImagenSubida();
            setArchivos([]);
            setComentarios({});
            onClose();
        } catch (error) {
            console.error('Error al subir imágenes:', error.message);
            alert(error.response?.data?.message || 'Error al subir las imágenes');
        } finally {
            setSubiendo(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Subir Imágenes de Evidencia
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                        }`}
                    onDragOver={manejarDragOver}
                    onDragLeave={manejarDragLeave}
                    onDrop={manejarDrop}
                    onPaste={manejarPaste}
                >
                    <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-300 mb-2">
                        Arrastra y suelta imágenes aquí, o haz clic para seleccionar
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        También puedes pegar imágenes con Ctrl+V
                    </p>
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Seleccionar Archivos
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={manejarCambioArchivo}
                        className="hidden"
                    />
                </div>

                {archivos.length > 0 && (
                    <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                            Archivos seleccionados ({archivos.length})
                        </h4>
                        <div className="space-y-4 max-h-60 overflow-y-auto">
                            {archivos.map((archivo, index) => (
                                <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center space-x-3 flex-1">
                                            <ImageIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-900 dark:text-white truncate">{archivo.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    {(archivo.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                            <button
                                                onClick={() => iniciarEdicionComentario(index)}
                                                className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
                                                title="Editar comentario"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => eliminarArchivo(index)}
                                                className="text-red-500 hover:text-red-700 dark:hover:text-red-400"
                                                title="Eliminar archivo"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Comentario (opcional):
                                        </label>
                                        {editandoComentario === index ? (
                                            <div className="space-y-2">
                                                <textarea
                                                    value={comentarios[index] || ''}
                                                    onChange={(e) => manejarCambioComentario(index, e.target.value)}
                                                    placeholder="Agrega un comentario para esta imagen..."
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white resize-none"
                                                    rows="2"
                                                    autoFocus
                                                />
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={finalizarEdicionComentario}
                                                        className="px-3 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                                                    >
                                                        Cancelar
                                                    </button>
                                                    <button
                                                        onClick={finalizarEdicionComentario}
                                                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                    >
                                                        Guardar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div
                                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white min-h-[2.5rem] cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                onClick={() => iniciarEdicionComentario(index)}
                                            >
                                                {comentarios[index] || 'Haz clic para agregar un comentario...'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={subirImagenes}
                        disabled={archivos.length === 0 || subiendo}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {subiendo ? 'Subiendo...' : `Subir ${archivos.length} imagen${archivos.length !== 1 ? 'es' : ''}`}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
